import { scaleLog, type ScaleLogarithmic } from "d3";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation, useNavigate } from "react-router";
import { useDebouncedCallback } from "use-debounce";
import {
  ActionButton,
  HeaderDivider,
  Item,
  MenuListBox,
  MenuOptionItem,
  Popover,
  Section,
  Slider,
  SymbolIcon,
  useContextualMenu,
} from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { getRangeCenteredAt, getRangeLength, getScaledRangeLength } from "@/app-utils/range.ts";
import type {
  Contig,
  GenomeBrowserStatistics,
  InputSearchEntityTypeInput,
  JobResult,
  Organism,
  Range,
  RegionFlag,
} from "@/graphql.ts";
import {
  clearGenomeBrowserSelection,
  contigChanged,
  type ISortableTrack,
  searchSequencedGenomes,
  setTrackList,
  showAnnotationStateChanged,
  showFlagsStateChanged,
  trackHeaderWidthChanged,
  viewRangeChanged,
} from "@/redux/modules/genome-browser.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_HEADER_DEFAULT_WIDTH, TRACK_HEADER_MIN_WIDTH } from "@/settings/genome-viewer-settings.ts";
import { Hotkeys } from "@/settings/hotkeys.ts";
import {
  GENOME_BROWSER_SIZE_OPTIONS,
  MIN_VIEW_RANGE_SIZE,
  SCALE_FACTOR_SLIDER_ZOOM,
  TRACK_TYPE,
  UNKNOWN_GENOME_ID,
} from "@/settings/settings.ts";

import { useGenomeViewerData } from "./_mod.js";
import ContigRangeSelector from "./ContigRangeSelector.js";
import { GenomeSelector } from "./GenomeSelector.js";
import { GraphicalRangeSelector } from "./GraphicalRangeSelector.js";
import { FeatureSearch } from "./search/FeatureSearch.js";
import TrackSelectionDialog from "./TrackSelectionDialog.js";

interface IGenomeViewerToolbar {
  startupEmpty?: boolean;
  selectionTooltipText?: string;
  genomes?: Organism[];
  jobResult: JobResult;
}

const SLIDER_MIN_VALUE = 0;
const SLIDER_MAX_VALUE = 1000;
const RANGE_DEBOUNCE_TIMER = 200;

export function GenomeViewerToolbar(props: IGenomeViewerToolbar) {
  const dispatch = useDispatch<ReduxDispatch>();
  const { range, contig, layout } = useGenomeViewerData();
  const flags = useSelector<IAppState, RegionFlag[]>((state) => state.genomeBrowser.flags, shallowEqual);
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome, shallowEqual);
  const statistics = useSelector<IAppState, GenomeBrowserStatistics>(
    (state) => state.genomeBrowser.statistics,
    shallowEqual,
  );
  const tracks = useSelector<IAppState, ISortableTrack[]>((state) => state.genomeBrowser.tracks, shallowEqual);
  const location = useLocation();
  const navigate = useNavigate();
  const menuWrapperRef = useRef<HTMLButtonElement>(null);
  const { state, triggerProps, menuProps } = useContextualMenu(menuWrapperRef);

  const showFlags = useSelector<IAppState, boolean>(
    (state) => state.auth.session?.params?.showFlagsTrack ?? false,
    shallowEqual,
  );

  const showAnnotations = useSelector<IAppState, boolean>(
    (state) => state.auth.session?.params?.showCommunityAnnotations ?? false,
    shallowEqual,
  );
  const selectedKeys = useMemo(() => {
    const selection = [];

    if (showFlags) {
      selection.push("genome-viewer-show-flags");
    }
    if (showAnnotations) {
      selection.push("genome-viewer-show-features-annotations");
    }

    return selection;
  }, [showFlags, showAnnotations]);

  const isLabelsHidden = useMemo(() => layout.headerWidth === TRACK_HEADER_MIN_WIDTH, [layout.headerWidth]);

  const [showTrackSelectionDialog, setShowTrackSelectionDialog] = useState<boolean>(false);

  const debouncedViewRangeChanged = useDebouncedCallback((range: Range) => {
    dispatch(viewRangeChanged(range));
  }, RANGE_DEBOUNCE_TIMER);

  const debouncedSliderValueChanged = useDebouncedCallback((value: number) => {
    const newLength = scale.invert(value);
    const newRange = getRangeCenteredAt(range, newLength, contig.length);
    dispatch(viewRangeChanged(newRange));
  }, RANGE_DEBOUNCE_TIMER);

  function onTrackSelection(tracks: ISortableTrack[]) {
    dispatch(setTrackList(tracks));
    setShowTrackSelectionDialog(false);
  }

  // adapt the url according to the selected genome
  const handleGenomeSelect = useCallback(
    (genome: Organism) => {
      if (!genome || genome.id === UNKNOWN_GENOME_ID) {
        return;
      }
      // this pattern comes first because depending on the basename the summary pattern "/:type/:id/genome/*" can match e.g. /dev/visualizer/genome/123 !
      const globalBrowserPathParams = matchPath("/visualizer/genome/*", location.pathname);
      if (globalBrowserPathParams) {
        navigate(`/visualizer/genome/${genome.id}`);
        return;
      }
      const summaryBrowserPathParams = matchPath("/:type/:id/genome/*", location.pathname);
      if (summaryBrowserPathParams) {
        navigate(`/${summaryBrowserPathParams.params.type}/${summaryBrowserPathParams.params.id}/genome/${genome.id}`);
        return;
      }
    },
    [location],
  );

  const isGenomeBrowserReady: boolean = !!genome && !!statistics;
  const genomeBrowserSize: string = getGenomeBrowserWidthEnum(layout.viewWidth);
  const filteredTypes: InputSearchEntityTypeInput[] = tracks
    .filter((t) => t.type !== TRACK_TYPE.SEQUENCE)
    .filter((t) => !t.hidden)
    .map((t) => {
      const searchEntityType: InputSearchEntityTypeInput = { type: t.type, subTypeId: t.subTypeId };
      return searchEntityType;
    });

  const scale = useMemo<ScaleLogarithmic<number, number>>(
    () =>
      scaleLog()
        .base(SCALE_FACTOR_SLIDER_ZOOM)
        .domain([contig?.length ?? 0, MIN_VIEW_RANGE_SIZE])
        .range([SLIDER_MIN_VALUE, SLIDER_MAX_VALUE]),
    [contig?.length],
  );
  const controlsCallback = useCallback(
    (factor: number) => {
      const newLength = getScaledRangeLength(range, factor, MIN_VIEW_RANGE_SIZE, contig.length);
      const newRange = getRangeCenteredAt(range, newLength, contig.length);
      dispatch(viewRangeChanged(newRange));
    },
    [contig?.length, dispatch, range],
  );
  const [sliderCurrentValue, setSliderCurrentValue] = useState(SLIDER_MIN_VALUE);

  useEffect(() => {
    if (range) setSliderCurrentValue(scale(getRangeLength(range)));
  }, [range, scale]);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", columnGap: 10, margin: "0 auto" }}>
        {(isGenomeBrowserReady || props.startupEmpty) && (
          <div style={{ width: 250 }}>
            <span id="genome-viewer-current-genome-input" className="hotspot-beacon" />
            <GenomeSelector
              selectedGenome={genome}
              onSearch={(searchTerm: string) => dispatch(searchSequencedGenomes(searchTerm))}
              onSelect={handleGenomeSelect}
              genomes={props.genomes}
            />
          </div>
        )}

        {isGenomeBrowserReady && (
          <ContigRangeSelector
            key={`contig-range-selector-${genome.id}`}
            genomeId={genome.id === UNKNOWN_GENOME_ID ? undefined : genome.id}
            isVirtualGenome={genome.isVirtual}
            contig={contig}
            range={range}
            size={genomeBrowserSize}
            minRangeLength={MIN_VIEW_RANGE_SIZE}
            onRangeChange={debouncedViewRangeChanged}
            onContigChange={(contig: Contig) => dispatch(contigChanged(contig))}
            contigSelectorWidth={240}
          />
        )}

        {isGenomeBrowserReady && (
          <>
            {genomeBrowserSize !== GENOME_BROWSER_SIZE_OPTIONS.XSMALL &&
            genomeBrowserSize !== GENOME_BROWSER_SIZE_OPTIONS.XXSMALL ? (
              <div style={{ width: 160 }}>
                <Slider
                  step={1}
                  value={[sliderCurrentValue]}
                  minValue={0}
                  maxValue={1000}
                  onChange={([value]) => {
                    setSliderCurrentValue(value);
                    debouncedSliderValueChanged(value);
                  }}
                  noHeader
                  tooltip={(value) => {
                    const newLength = scale.invert(value);
                    const newRange = getRangeCenteredAt(range, newLength, contig.length);
                    return `${formatNumberByThousands(newRange.start ?? 1)} – ${formatNumberByThousands(
                      newRange.stop ?? contig.length + 1,
                    )}`;
                  }}
                  controls={{
                    left: {
                      label: "Zoom Out",
                      icon: "indeterminate_check_box",
                      onPress: () => {
                        controlsCallback(SCALE_FACTOR_SLIDER_ZOOM);
                      },
                      isDisabled: sliderCurrentValue === SLIDER_MIN_VALUE,
                    },
                    right: {
                      label: "Zoom In",
                      icon: "add_box",
                      onPress: () => {
                        controlsCallback(1 / SCALE_FACTOR_SLIDER_ZOOM);
                      },
                      isDisabled: sliderCurrentValue === SLIDER_MAX_VALUE,
                    },
                  }}
                />
              </div>
            ) : null}
            <HeaderDivider />
            <FeatureSearch
              size={genomeBrowserSize}
              organismId={genome.id}
              isVirtualGenome={genome.isVirtual}
              types={filteredTypes}
            />
          </>
        )}
      </div>

      {isGenomeBrowserReady && (
        <div style={{ display: "flex", columnGap: 5, alignItems: "flex-end" }}>
          <div style={{ position: "relative", display: "flex", columnGap: 5 }}>
            <span id="genome-viewer-manage-tracks-button" className="hotspot-beacon" />
            <ActionButton
              id="genome-viewer-menu-button"
              onPress={() => setShowTrackSelectionDialog(true)}
              icon={"layers"}
              label={"Tracks"}
            />
            <ActionButton
              id="genome-viewer-more-button"
              icon={"keyboard_arrow_down"}
              aria-label={"More tracks settings"}
              onPress={() => state.toggle()}
              {...triggerProps}
              ref={menuWrapperRef}
              label={""}
            />
            <Popover state={state} triggerRef={menuWrapperRef} placement={"bottom start"} offset={4} crossOffset={-5}>
              <MenuListBox
                {...menuProps}
                selectionMode={"multiple"}
                selectedKeys={selectedKeys}
                onAction={(key) => {
                  switch (key) {
                    case "genome-viewer-show-flags":
                      dispatch(showFlagsStateChanged(!showFlags));
                      break;
                    case "genome-viewer-show-features-annotations":
                      dispatch(showAnnotationStateChanged(!showAnnotations));
                      break;
                    case "genome-viewer-shrink-labels":
                      dispatch(
                        trackHeaderWidthChanged(isLabelsHidden ? TRACK_HEADER_DEFAULT_WIDTH : TRACK_HEADER_MIN_WIDTH),
                      );
                      state.close();
                      break;
                    case "genome-viewer-clear-selection":
                      dispatch(clearGenomeBrowserSelection());
                      state.close();
                      break;
                    default:
                      break;
                  }
                }}
              >
                <Section key={1}>
                  <Item key={"genome-viewer-show-flags"}>
                    <MenuOptionItem title={"Flags Track"} icon={"flag"} data-checked={String(showFlags)} />
                  </Item>
                  <Item key={"genome-viewer-show-features-annotations"}>
                    <MenuOptionItem
                      title={"Community Annotation"}
                      icon={"fiber_manual_record"}
                      data-checked={String(showAnnotations)}
                    />
                  </Item>
                </Section>
                <Section key={2}>
                  <Item key={"genome-viewer-shrink-labels"}>
                    <MenuOptionItem
                      title={`${isLabelsHidden ? "Show" : "Hide"} Track Header`}
                      icon={<SymbolIcon icon={"dock_to_right"} filled={!isLabelsHidden} />}
                      shortcut={"Alt+\\"}
                    />
                  </Item>
                  <Item key={"genome-viewer-clear-selection"}>
                    <MenuOptionItem
                      title={"Clear Selection"}
                      icon={"deselect"}
                      shortcut={Hotkeys.genomeViewer.clearSelection}
                    />
                  </Item>
                </Section>
              </MenuListBox>
            </Popover>
          </div>
          {contig && range && (
            <GraphicalRangeSelector
              {...GraphicalRangeSelector.getPropsWithDefaults({
                contig,
                range,
                maxRange: contig.length,
                minRangeLength: 10,
                boxModel: {
                  width: layout.viewWidth - 170,
                },
                flags: showFlags ? flags : [],
              })}
              onRangeChange={debouncedViewRangeChanged}
            />
          )}
        </div>
      )}

      {showTrackSelectionDialog && (
        <TrackSelectionDialog
          genome={genome}
          show={true}
          tracks={tracks}
          onCancel={() => setShowTrackSelectionDialog(false)}
          onApply={onTrackSelection}
          hideBlast={!props.jobResult}
        />
      )}
    </>
  );
}

function getGenomeBrowserWidthEnum(genomeBrowserWidth: number): string {
  const browserWidth: number = genomeBrowserWidth;
  if (browserWidth && browserWidth > 1300) {
    return GENOME_BROWSER_SIZE_OPTIONS.XLARGE;
  }

  if (browserWidth && browserWidth > 1200) {
    return GENOME_BROWSER_SIZE_OPTIONS.LARGE;
  }

  if (browserWidth && browserWidth > 900) {
    return GENOME_BROWSER_SIZE_OPTIONS.MEDIUM;
  }

  if (browserWidth && browserWidth > 800) {
    return GENOME_BROWSER_SIZE_OPTIONS.SMALL;
  }

  if (browserWidth && browserWidth > 700) {
    return GENOME_BROWSER_SIZE_OPTIONS.XSMALL;
  }

  return GENOME_BROWSER_SIZE_OPTIONS.XXSMALL;
}
